import execCommand from 'execcommand-copy';
import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { showConfirmWithCheckbox } from 'APP/Tasks/app/app';
import { ForwardMessagePopup } from 'APP/main/PopupManager/Popups/ForwardMessagePopup/ForwardMessagePopup';
import { DeleteMessagesMode } from 'APP/model/message/messageModel.types';
import { Schedule } from 'APP/store/Groups/Schedule';
import { ChatMessage } from 'APP/store/Messages/Message/ChatMessage/ChatMessage';
import { MultiSelect } from 'APP/store/Messages/MultiSelect/MultiSelect';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { getMessageTextForCopy } from 'UTILS/message/message';

interface IMultiSelectPresenter {
  childPopup: PopupStore | null;
  multiSelect: MultiSelect;
  hasSelectedMessages: boolean;
  selectedMessagesIds: string[];
  selectedMessages: ChatMessage[];
  amountOfMessages: number;
  groupId: string | null;
  canBeDeleted: boolean;
  canBeCopied: boolean;
  canBeForwarded: boolean;
  canBeSendNow: boolean;
  group: Group | null;
  copyToClipboard(): void;
  close(): void;
  forward(): void;
  sendNow(): void;
  showDeleteConfirm(): Promise<{
    canDelete: boolean;
    deleteMode: DeleteMessagesMode;
  }>;
  delete(): Promise<void>;
}

export function useMultiSelectPresenter(): IMultiSelectPresenter {
  const { t } = useTranslation();

  const presenter = useLocalStore<IMultiSelectPresenter>(() => ({
    childPopup: null,

    get multiSelect(): MultiSelect {
      return Entities.GroupsStore.activeGroup!.messagesStore.multiSelect;
    },

    get hasSelectedMessages(): boolean {
      return presenter.multiSelect.hasSelectedMessages || false;
    },

    get selectedMessagesIds(): string[] {
      return Array.from(presenter.multiSelect.selectedMessagesIds || []);
    },

    get selectedMessages(): ChatMessage[] {
      return (
        presenter.multiSelect.selectedMessages.sort(
          (a, b) => a.expectedServerTime - b.expectedServerTime
        ) || []
      );
    },

    get amountOfMessages(): number {
      return presenter.selectedMessagesIds.length;
    },

    get groupId(): string | null {
      return Entities.GroupsStore.activeGroupId;
    },

    get group(): Group | null {
      return Entities.GroupsStore.activeGroup;
    },

    get canBeDeleted(): boolean {
      if (presenter.group?.isServiceMessages) {
        return false;
      }

      return presenter.selectedMessages.every(
        (message) => message.canBeDeleteForMe || message.canBeDelete
      );
    },

    get canBeCopied(): boolean {
      return presenter.selectedMessages.every((message) => message.canBeCopied);
    },

    get canBeForwarded(): boolean {
      return presenter.selectedMessages.every((message) => message.canBeForward);
    },

    get canBeSendNow(): boolean {
      return presenter.selectedMessages.every((message) => message.canBeSendNow);
    },

    copyToClipboard: (): void => {
      const textForCopyArr = presenter.selectedMessages.map((message) => {
        return getMessageTextForCopy(message, presenter.selectedMessages.length > 1);
      });
      const textForCopy = textForCopyArr.join('\n\n');

      execCommand.copy(textForCopy);
      presenter.multiSelect.clearSelectedMessages();
    },

    close: (): void => {
      presenter.multiSelect.clearSelectedMessages();
    },

    forward: (): void => {
      if (!presenter.groupId) {
        return;
      }
      presenter.childPopup = Tasks.app.openPopup(ForwardMessagePopup, {
        params: { groupId: presenter.groupId, messageIds: presenter.selectedMessagesIds },
      });
    },

    sendNow: (): void => {
      Tasks.messaging.sendNowScheduledMessages(presenter.groupId!, presenter.selectedMessages);
    },

    async showDeleteConfirm(): Promise<{
      canDelete: boolean;
      deleteMode: DeleteMessagesMode;
    }> {
      let canDeleteAllMessagesForAll = true;
      let canDeleteAllMessagesByMe = true;
      let hasRegularMessages = false;
      let isAllMessagesDeletedByMe = true;
      let isAllRegularMessages = true;

      const isMultipleMessages = presenter.selectedMessages.length > 1;

      presenter.selectedMessages.forEach((selectMessage) => {
        if (!selectMessage.canBeDelete) {
          canDeleteAllMessagesForAll = false;
        }
        if (!selectMessage.canBeDeleteForMe) {
          canDeleteAllMessagesByMe = false;
        }

        if (selectMessage.isDeletedByMe) {
          isAllRegularMessages = false;
          return;
        }

        isAllMessagesDeletedByMe = false;
        hasRegularMessages = true;
      });

      let canDelete = false;
      let deleteMode = DeleteMessagesMode.All;

      if (
        (isAllRegularMessages && !canDeleteAllMessagesByMe) ||
        (isAllMessagesDeletedByMe && canDeleteAllMessagesForAll)
      ) {
        const confirmTextTranslationKey = isMultipleMessages
          ? 'message_delete_several_alert'
          : 'delete_message';

        canDelete = await Tasks.app.showConfirm({ text: t(confirmTextTranslationKey) });
        return { canDelete, deleteMode };
      }

      if (hasRegularMessages && !canDeleteAllMessagesForAll) {
        const confirmTextTranslationKey = isMultipleMessages
          ? 'common_delete_for_me_alert_plural'
          : 'common_delete_for_me_alert';

        canDelete = await Tasks.app.showConfirm({ text: t(confirmTextTranslationKey) });
        deleteMode = DeleteMessagesMode.Me;
        return { canDelete, deleteMode };
      }

      const confirmTextTranslationKey = isMultipleMessages
        ? 'message_delete_several_alert'
        : 'delete_message';

      const { isConfirmed, isChecked } = await showConfirmWithCheckbox({
        text: t(confirmTextTranslationKey),
        checkBoxText: t('delete_for_me'),
      });

      canDelete = isConfirmed;
      deleteMode = isChecked ? DeleteMessagesMode.Me : DeleteMessagesMode.All;

      return { canDelete, deleteMode };
    },

    async delete(): Promise<void> {
      const { canDelete, deleteMode } = await presenter.showDeleteConfirm();

      if (!canDelete) {
        return;
      }

      const group = Entities.GroupsStore.activeGroup;

      if (!group || !presenter.selectedMessages) {
        return;
      }

      const data = {
        groupId: group.id,
        messages: presenter.selectedMessages,
      };

      if (group instanceof Schedule && group.isSchedule) {
        await Tasks.messaging.deleteScheduledMessages(data);
      } else {
        await Tasks.messaging.deleteMessages({
          ...data,
          mode: deleteMode,
        });
      }
    },
  }));

  useEffect(() => {
    return (): void => presenter.childPopup?.close();
  }, []);

  return presenter;
}
